<template>
  <div class="flex flex-col sm:h-screen bg-gray-50 dark:bg-[#0d1117]">
    <!-- Sticky page header — this page now lives outside the Settings shell,
         so (unlike when it was a Settings tab) it owns its own header/padding
         entirely, matching the pattern other standalone org pages use
         (e.g. IT Components' OrgComponentsPanel). -->
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-10">
      <div class="flex items-center gap-2.5 h-14">
        <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <div class="min-w-0">
          <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none truncate">Activity Logs</h1>
          <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">A history of who changed what in your organization</p>
        </div>
      </div>
    </div>

    <!-- Single scroll region — the toolbar stays sticky at its top while the
         log list scrolls underneath and loads more as you near the bottom,
         same structure as OrgComponentsPanel's own IT Components list.
         Visible to every accepted org member, not just admins/the owner —
         the backend doesn't restrict this endpoint either. -->
    <div class="flex-1 overflow-y-auto">
      <div class="sticky top-0 z-[5] bg-gray-50/95 dark:bg-[#0d1117]/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-10 py-3 flex flex-wrap items-center gap-2">
        <div class="relative flex-1 min-w-[160px] max-w-xs">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="searchInput" type="search" placeholder="Search by person or action"
            class="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none"/>
          <button v-if="searchInput" @click="clearSearch" type="button" aria-label="Clear search"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <SortFilterButton :options="SORT_OPTIONS" :model-value="currentSortValue" @update:model-value="applySort" />
        <ActionFilterButton :actions="store.availableActions" :selected="store.filters.actions"
          @toggle="store.toggleAction($event)" @clear="store.clearActionFilters()" />

        <span class="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-auto">
          {{ store.pageInfo.total_records || 0 }} {{ store.pageInfo.total_records === 1 ? 'entry' : 'entries' }}
        </span>
      </div>

      <div class="px-4 sm:px-6 lg:px-10 py-6">
        <!-- Loading skeleton -->
        <div v-if="store.loading && store.logs.length === 0" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
          <div v-for="n in 6" :key="n" class="grid grid-cols-[7rem_10rem_1fr] items-center gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800/60 first:border-t-0">
            <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3.5 w-2/3 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="store.error" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-16 text-center px-4">
          <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
            </svg>
          </div>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
          <button @click="retry" type="button" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Try again
          </button>
        </div>

        <!-- Empty state -->
        <div v-else-if="store.logs.length === 0" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-16 text-center">
          <p class="text-sm text-gray-400 dark:text-gray-500">
            {{ store.hasActiveFilters ? 'No activity matches your filters.' : 'No activity has been recorded yet.' }}
          </p>
          <button v-if="store.hasActiveFilters" @click="clearAll" type="button"
            class="mt-3 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
            Clear filters
          </button>
        </div>

        <!-- Log list -->
        <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="grid grid-cols-[7rem_10rem_1fr] items-center gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/40 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <span>Date</span>
            <span>Person</span>
            <span>Activity</span>
          </div>

          <div v-for="log in store.logs" :key="log.id"
            class="grid grid-cols-[7rem_10rem_1fr] items-start gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors">
            <p class="text-xs text-gray-400 dark:text-gray-500 pt-0.5" :title="log.created_at">{{ log.created_at_ago }}</p>

            <div class="flex items-center gap-2 min-w-0">
              <div :class="avatarColorClass(log)" class="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {{ initials(log.actor_name) }}
              </div>
              <p class="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{{ log.actor_name || 'System' }}</p>
            </div>

            <div class="min-w-0">
              <p class="text-sm text-gray-700 dark:text-gray-200">{{ log.description }}</p>
              <div v-if="Object.keys(log.changeset).length" class="mt-1.5 flex flex-col gap-1">
                <div v-for="(change, field) in log.changeset" :key="field" class="flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span class="font-semibold text-gray-500 dark:text-gray-400">{{ field }}:</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-mono line-through decoration-red-400/70">
                    {{ change[0] || '—' }}
                  </span>
                  <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-mono">
                    {{ change[1] || '—' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref="sentinel" class="h-8"></div>
        <div v-if="store.loadingMore" class="flex justify-center py-4">
          <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useActivityLogsStore } from 'stores/activityLogs.js'
import SortFilterButton from 'controllers/vue_components/organizations/it_components/SortFilterButton.vue'
import ActionFilterButton from 'controllers/vue_components/organizations/activity_logs/ActionFilterButton.vue'

const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Newest first', by: 'created_at', order: 'desc' },
  { value: 'created_at-asc', label: 'Oldest first', by: 'created_at', order: 'asc' },
  { value: 'action-asc', label: 'Action (A→Z)', by: 'action', order: 'asc' },
  { value: 'action-desc', label: 'Action (Z→A)', by: 'action', order: 'desc' },
]

const AVATAR_COLORS = ['peacock', 'sage', 'tangerine', 'grape', 'flamingo', 'blueberry', 'basil', 'tomato', 'lavender', 'graphite', 'banana']

const globalStore = orgStore()
const store = useActivityLogsStore()

const searchInput = ref(store.filters.search)
const sentinel = ref(null)
let searchDebounce = null
let observer = null

const currentSortValue = computed(() => `${store.sort.by}-${store.sort.order}`)

async function applySort(value) {
  const option = SORT_OPTIONS.find((o) => o.value === value)
  if (option) await store.setSort(option.by, option.order)
}

function hashToIndex(str, length) {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) % length
  return hash
}

function avatarColorClass(log) {
  const key = (log.actor_email || log.actor_name || 'system').toLowerCase()
  return AVATAR_COLORS[hashToIndex(key, AVATAR_COLORS.length)]
}

function initials(name) {
  if (!name) return '—'
  const words = name.trim().split(/\s+/).filter(Boolean)
  const result = words.map((w) => w[0]).slice(0, 2).join('').toUpperCase()
  return result || '—'
}

watch(searchInput, (value) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => store.setSearch(value.trim()), 300)
})

function clearSearch() {
  searchInput.value = ''
}

async function clearAll() {
  searchInput.value = ''
  await store.clearFilters()
}

async function retry() {
  await store.fetchPage(true)
}

onMounted(() => {
  store.init(globalStore.current_org.id)

  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) store.fetchMore()
  }, { threshold: 0.1 })
  if (sentinel.value) observer.observe(sentinel.value)
})

onUnmounted(() => {
  observer?.disconnect()
  clearTimeout(searchDebounce)
})
</script>
